"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getPendingReceiptYearForDate = exports.getPendingReceiptDonationDate = exports.getPendingReceiptYearBoundaries = exports.getPendingReceiptYearTurningPoint = exports.formatDateTimeToLocaleString = exports.getDateTime = exports.getLuxonFormatFromLocale = exports.getDateFormatHint = exports.DATE_FIELD_PLACEHOLDER_LETTERS = exports.getDateFormatFromLocale = exports.getDatePickerAdapterLocale = exports.isAmPm = exports.getDateFormatByCountry = exports.parseDateByCountry = void 0;
const luxon_1 = require("luxon");
const country_1 = require("../enums/country");
const locales_1 = require("../enums/locales");
const locales_2 = require("./locales");
const DATE_ORDER_REFERENCE_DATE = new Date(Date.UTC(2023, 4, 9));
const isMonthFirstLocale = (isoLocale) => {
    const parts = new Intl.DateTimeFormat(isoLocale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).formatToParts(DATE_ORDER_REFERENCE_DATE);
    const monthIndex = parts.findIndex((part) => part.type === "month");
    const dayIndex = parts.findIndex((part) => part.type === "day");
    const yearIndex = parts.findIndex((part) => part.type === "year");
    return monthIndex < dayIndex && dayIndex < yearIndex;
};
const parseDateByCountry = (date, country) => {
    return luxon_1.DateTime.fromFormat(date, (0, exports.getDateFormatByCountry)(country)).toJSDate();
};
exports.parseDateByCountry = parseDateByCountry;
const getDateFormatByCountry = (country) => {
    const isoLocale = country ? (0, locales_2.getIsoLocaleFromCountry)(country, locales_1.Locales.EN) : locales_1.UsaIsoLocale.EN;
    return (0, exports.getDateFormatFromLocale)(isoLocale);
};
exports.getDateFormatByCountry = getDateFormatByCountry;
const isAmPm = (isoLocale) => {
    return new Intl.DateTimeFormat(isoLocale, { hour: "numeric" }).resolvedOptions().hour12 ?? false;
};
exports.isAmPm = isAmPm;
/**
 * Locale to pass to the date picker LocalizationProvider (adapterLocale).
 * - en-US, en-CA: week starts on Sunday.
 * - fr-CA (Canada French), en-GB, etc.: week starts on Monday (adapter uses locale convention).
 */
const getDatePickerAdapterLocale = (isoLocale) => {
    if (isoLocale === locales_1.CadIsoLocale.FR) {
        return "fr-FR";
    }
    return isoLocale;
};
exports.getDatePickerAdapterLocale = getDatePickerAdapterLocale;
/**
 * Date-only format for date picker inputs (no time), derived from Intl.
 * - United States (en-US): MM/dd/yyyy (e.g. 01/31/2021)
 * - Canada, UK, Ireland, Australia, etc.: dd/MM/yyyy (e.g. 31/01/2021)
 */
const getDateFormatFromLocale = (isoLocale) => {
    if (isoLocale === locales_1.DeIsoLocale.DE || isoLocale === locales_1.DeIsoLocale.EN) {
        return "dd.MM.yyyy";
    }
    return isMonthFirstLocale(isoLocale) ? "MM/dd/yyyy" : "dd/MM/yyyy";
};
exports.getDateFormatFromLocale = getDateFormatFromLocale;
exports.DATE_FIELD_PLACEHOLDER_LETTERS = {
    [locales_1.Locales.EN]: { day: "D", month: "M", year: "Y" },
    [locales_1.Locales.FR]: { day: "D", month: "M", year: "Y" },
    [locales_1.Locales.ES]: { day: "D", month: "M", year: "Y" },
    [locales_1.Locales.DE]: { day: "T", month: "M", year: "J" },
};
const DATE_HINT_LETTERS = {
    [locales_1.Locales.EN]: null,
    [locales_1.Locales.FR]: null,
    [locales_1.Locales.ES]: null,
    [locales_1.Locales.DE]: { day: "T", year: "J" },
};
const getDateFormatHint = (format, locale) => {
    const letters = DATE_HINT_LETTERS[locale];
    if (!letters) {
        return format;
    }
    return format.replace(/d/g, letters.day).replace(/y/g, letters.year);
};
exports.getDateFormatHint = getDateFormatHint;
const getLuxonFormatFromLocale = (isoLocale) => {
    const timePart = (0, exports.isAmPm)(isoLocale) ? "hh:mm a" : "HH:mm";
    return `${(0, exports.getDateFormatFromLocale)(isoLocale)} ${timePart}`;
};
exports.getLuxonFormatFromLocale = getLuxonFormatFromLocale;
const getDateTime = (date, options) => {
    if (typeof date === "number") {
        return luxon_1.DateTime.fromMillis(date, options);
    }
    // Handle string timestamps (milliseconds)
    if (typeof date === "string" && /^\d+$/.test(date)) {
        return luxon_1.DateTime.fromMillis(parseInt(date, 10), options);
    }
    return luxon_1.DateTime.fromJSDate(new Date(date), options);
};
exports.getDateTime = getDateTime;
const formatDateTimeToLocaleString = (date, locale, option, timeZone) => {
    if (option && locale === locales_1.CadIsoLocale.FR) {
        // specific 24 time based for french
        return timeZone
            ? (0, exports.getDateTime)(date)
                .setLocale(locale.toLowerCase())
                .setZone(timeZone)
                .toLocaleString(option ? { ...option, hour12: false } : {})
            : (0, exports.getDateTime)(date)
                .setLocale(locale.toLowerCase())
                .toLocaleString(option ? { ...option, hour12: false } : {});
    }
    return timeZone
        ? (0, exports.getDateTime)(date).setLocale(locale.toLowerCase()).setZone(timeZone).toLocaleString(option)
        : (0, exports.getDateTime)(date).setLocale(locale.toLowerCase()).toLocaleString(option);
};
exports.formatDateTimeToLocaleString = formatDateTimeToLocaleString;
const getPendingReceiptYearTurningPoint = ({ year, organizationCountry, }) => {
    const month = organizationCountry === country_1.OrganizationCountry.Australia ? 6 : 0;
    return new Date(Date.UTC(year, month, 1, 10, 0, 0));
};
exports.getPendingReceiptYearTurningPoint = getPendingReceiptYearTurningPoint;
const getPendingReceiptYearBoundaries = ({ year, organizationCountry, }) => {
    if (organizationCountry === country_1.OrganizationCountry.Australia) {
        return [
            (0, exports.getPendingReceiptYearTurningPoint)({ year: year - 1, organizationCountry }),
            (0, exports.getPendingReceiptYearTurningPoint)({ year, organizationCountry }),
        ];
    }
    return [
        (0, exports.getPendingReceiptYearTurningPoint)({ year, organizationCountry }),
        (0, exports.getPendingReceiptYearTurningPoint)({ year: year + 1, organizationCountry }),
    ];
};
exports.getPendingReceiptYearBoundaries = getPendingReceiptYearBoundaries;
const getPendingReceiptDonationDate = ({ year, organizationCountry, }) => {
    return organizationCountry === country_1.OrganizationCountry.Australia ? `${year}-06-30` : `${year}-12-30`;
};
exports.getPendingReceiptDonationDate = getPendingReceiptDonationDate;
const getPendingReceiptYearForDate = ({ date, organizationCountry, }) => {
    const calendarYear = date.getFullYear();
    const turningPoint = (0, exports.getPendingReceiptYearTurningPoint)({ year: calendarYear, organizationCountry });
    const australianFiscalYearShift = organizationCountry === country_1.OrganizationCountry.Australia ? 1 : 0;
    return (date.getTime() > turningPoint.getTime() ? calendarYear : calendarYear - 1) + australianFiscalYearShift;
};
exports.getPendingReceiptYearForDate = getPendingReceiptYearForDate;
